import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectVideoModeCopyTag } from "@/store/ielements-slice-selector";
import { selectLightweightElements } from "@/store/lightweight-elements-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  useIsReportToolActive,
  useIsSnapshotToolActive,
} from "@/store/ui/ui-utils";
import { generateReportTree } from "@/tools/create-report/create-report-tree-generation";
import { generateSnapshotTree } from "@/tools/create-snapshot/create-snapshot-tree-generation";
import { TreeData } from "@/utils/tree-generation/tree-data";
import { generateProjectTree } from "@/utils/tree-generation/tree-generation";
import {
  selectAllAreaDatasets,
  selectClusters,
  selectRootId,
} from "@faro-lotv/project-source";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
  type JSX,
} from "react";

type OpenMap = Record<string, boolean>;

export type TreeContextData = {
  /** The tree data */
  tree: TreeData[];

  /** The open state of the tree */
  openState: OpenMap;

  /** Set the open state of the tree */
  setOpenState(openState: OpenMap): void;
};

const TreeContext = createContext<TreeContextData | undefined>(undefined);

/**
 * @returns a context useful to set and read the current tree data
 */
export function useTreeContext(): TreeContextData {
  const context = useContext(TreeContext);
  if (!context) {
    throw new Error("TreeContext is not initialized.");
  }
  return context;
}

/**
 * @returns a component to keep the tree data synced
 */
export function TreeContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const lightweightElements = useAppSelector(selectLightweightElements);
  const areaDataSets = useAppSelector(selectAllAreaDatasets);
  const rootId = useAppSelector(selectRootId);
  const clusters = useAppSelector(selectClusters);
  const hasFullTree = useAppSelector(selectHasFeature(Features.FullTree));

  const isReportToolActive = useIsReportToolActive();
  const isSnapshotToolActive = useIsSnapshotToolActive();

  const videoModeCopyTagId = useAppSelector(selectVideoModeCopyTag);

  const tree = useMemo(() => {
    if (isSnapshotToolActive) {
      return generateSnapshotTree({
        parentId: rootId ?? "",
        elements: lightweightElements,
        videoModeCopyTagId,
      });
    }
    if (isReportToolActive) {
      return generateReportTree({
        parentId: rootId ?? "",
        elements: lightweightElements,
        areaDataSets,
        clusters,
        videoModeCopyTagId,
      });
    }
    return generateProjectTree({
      parentId: rootId ?? "",
      elements: lightweightElements,
      areaDataSets,
      clusters,
      fullTree: hasFullTree,
      videoModeCopyTagId,
    });
  }, [
    rootId,
    lightweightElements,
    areaDataSets,
    clusters,
    hasFullTree,
    isReportToolActive,
    isSnapshotToolActive,
    videoModeCopyTagId,
  ]);

  const [openState, setOpenState] = useState<OpenMap>({
    [tree[0]?.id]: true,
  });

  const value = useMemo<TreeContextData>(
    () => ({
      tree,
      openState,
      setOpenState,
    }),
    [openState, tree],
  );

  return <TreeContext value={value}>{children}</TreeContext>;
}
